/**
 * RTP frame encode / decode (RFC 3550 §5.1).
 *
 * Wire format (12-byte fixed header):
 *
 *    0                   1                   2                   3
 *    0 1 2 3 4 5 6 7 8 9 0 1 2 3 4 5 6 7 8 9 0 1 2 3 4 5 6 7 8 9 0 1
 *   +-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+
 *   |V=2|P|X|  CC   |M|     PT      |       sequence number         |
 *   +-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+
 *   |                           timestamp                           |
 *   +-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+
 *   |           synchronization source (SSRC) identifier            |
 *   +-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+-+
 *
 * All multi-byte fields are network-byte-order (big-endian).
 *
 * For PTT voice over WSS we always emit V=2, P=0, X=0, CC=0, M=0.
 * Payload type is 0 for µ-law (PCMU) and 8 for a-law (PCMA) per
 * RFC 3551, but the MCPTT manager doesn't actually inspect PT in
 * either direction — what matters is the SSRC matches what we
 * announced in DEVICE_CONFIG.Ssrc.
 */

export interface RtpHeader {
  payloadType: number;
  sequence:    number;
  timestamp:   number;
  ssrc:        number;
  marker:      boolean;
}

export interface RtpFrame {
  header:  RtpHeader;
  payload: Uint8Array;
}

/** Parse a UDP-formatted RTP packet. Returns null if the buffer is
 *  too short or the version isn't 2 (defensive — guards against
 *  WebSocket framing quirks). */
export function parseRtp(buf: Uint8Array): RtpFrame | null {
  if (buf.length < 12) return null;
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const b0 = view.getUint8(0);
  const version = (b0 >> 6) & 0x03;
  if (version !== 2) return null;
  const b1 = view.getUint8(1);
  const marker      = !!(b1 & 0x80);
  const payloadType = b1 & 0x7f;
  const sequence    = view.getUint16(2, false);
  const timestamp   = view.getUint32(4, false);
  const ssrc        = view.getUint32(8, false);
  const cc          = b0 & 0x0f;
  const headerLen   = 12 + cc * 4;
  if (buf.length < headerLen) return null;
  return {
    header:  { payloadType, sequence, timestamp, ssrc, marker },
    payload: buf.subarray(headerLen),
  };
}

/** Build a UDP-formatted RTP packet with the provided payload. */
export function buildRtp(
  payload:     Uint8Array,
  sequence:    number,
  timestamp:   number,
  ssrc:        number,
  payloadType: number,
  marker:      boolean = false,
): Uint8Array {
  const out = new Uint8Array(12 + payload.length);
  const view = new DataView(out.buffer);
  // V=2, P=0, X=0, CC=0
  view.setUint8(0, 0x80);
  view.setUint8(1, (marker ? 0x80 : 0) | (payloadType & 0x7f));
  view.setUint16(2, sequence & 0xffff, false);
  view.setUint32(4, timestamp >>> 0, false);
  view.setUint32(8, ssrc >>> 0, false);
  out.set(payload, 12);
  return out;
}

/** Random unsigned 32-bit value (suitable for RTP SSRC). */
export function randomSsrc(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}
